// src/core/helpers/htmlValidator.ts

/**
 * Resultado de la validación de HTML
 */
export interface ValidationResult {
  isValid: boolean;
  warnings: string[];
  errors: string[];
  details: {
    unclosedTags: string[];
    malformedAttributes: string[];
    specialCharacters: string[];
  };
}

/**
 * Valida la estructura del HTML sin modificar el contenido
 * Detecta problemas que pueden causar errores en el renderizado
 */
export const validateHtmlStructure = (html: string): ValidationResult => {
  const result: ValidationResult = {
    isValid: true,
    warnings: [],
    errors: [],
    details: {
      unclosedTags: [],
      malformedAttributes: [],
      specialCharacters: [],
    },
  };

  if (!html || typeof html !== 'string') {
    result.isValid = false;
    result.errors.push('HTML vacío o no es una cadena de texto');
    return result;
  }

  // Detectar tags no cerrados (básico)
  const tagPattern = /<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
  const closingTagPattern = /<\/[a-zA-Z][a-zA-Z0-9]*>/g;
  
  const allTags: string[] = [];
  const closingTags: string[] = [];
  const selfClosingTags: string[] = [];
  
  let match;
  
  // Encontrar todos los tags de apertura
  while ((match = tagPattern.exec(html)) !== null) {
    const tagName = match[1].toLowerCase();
    // Verificar si es self-closing
    if (match[0].endsWith('/>') || match[0].endsWith('/ >')) {
      selfClosingTags.push(tagName);
    } else {
      allTags.push(tagName);
    }
  }
  
  // Encontrar todos los tags de cierre
  while ((match = closingTagPattern.exec(html)) !== null) {
    const tagName = match[0].replace(/<\/|>/g, '').toLowerCase();
    closingTags.push(tagName);
  }
  
  // Tags que no necesitan cierre
  const voidElements = new Set([
    'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
    'link', 'meta', 'param', 'source', 'track', 'wbr'
  ]);
  
  // Verificar tags no cerrados
  const tagCounts: Record<string, number> = {};
  
  allTags.forEach(tag => {
    if (!voidElements.has(tag)) {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    }
  });
  
  closingTags.forEach(tag => {
    if (tagCounts[tag]) {
      tagCounts[tag]--;
    }
  });
  
  Object.entries(tagCounts).forEach(([tag, count]) => {
    if (count > 0) {
      result.details.unclosedTags.push(`${tag} (${count} sin cerrar)`);
      result.warnings.push(`Tag <${tag}> no cerrado (${count} instancia(s))`);
    }
  });

  // Detectar atributos mal formados
  const malformedAttributes: string[] = [];
  
  // Buscar atributos sin comillas o mal formados
  const unquotedAttrPattern = /\s+(\w+)\s*=\s*([^\s>]+)(?![^<]*>)/g;
  let attrMatch;
  while ((attrMatch = unquotedAttrPattern.exec(html)) !== null) {
    const attrValue = attrMatch[2];
    // Si el valor contiene espacios y no está entre comillas, es problemático
    if (attrValue.includes(' ') && !attrValue.startsWith('"') && !attrValue.startsWith("'")) {
      malformedAttributes.push(attrMatch[1]);
    }
  }
  
  if (malformedAttributes.length > 0) {
    result.details.malformedAttributes = Array.from(new Set(malformedAttributes));
    result.warnings.push(`Atributos posiblemente mal formados: ${result.details.malformedAttributes.join(', ')}`);
  }

  // Detectar caracteres especiales problemáticos
  const problematicChars = [
    { char: '\u0000', name: 'Null character' },
    { char: '\u200B', name: 'Zero-width space' },
    { char: '\uFEFF', name: 'BOM (Byte Order Mark)' },
  ];
  
  problematicChars.forEach(({ char, name }) => {
    if (html.includes(char)) {
      result.details.specialCharacters.push(name);
      result.warnings.push(`Carácter especial problemático detectado: ${name}`);
    }
  });

  // Si hay errores o warnings críticos, marcar como inválido
  if (result.errors.length > 0 || result.details.unclosedTags.length > 5) {
    result.isValid = false;
  }

  return result;
};

